import { Component, Input } from '@angular/core';
import { NgForm } from '@angular/forms';
import { NbDialogRef } from '@nebular/theme';
import {
  AnalysisCategory,
  LearningDraft,
  LearningInput,
  LearningService,
  SimilarLearning,
} from '../../../services/learning.service';

/** What the dialog hands back when the expert continues to the form. */
export interface LearningDraftResult {
  scope: 'bot' | 'client';
  botId: string;
  prefill: Partial<LearningInput>;
}

/**
 * "Daraus lernen" — step 1 of curating a learning from a conversation.
 *
 * The expert writes what was wrong and how it should be (required). The
 * backend shapes that into a proposal, lists the learnings the agent would
 * ALREADY get for this question, and says whether a learning is the right
 * tool at all (missing material belongs in the knowledge base, a fixed
 * recurring answer in the FAQ). Nothing is saved here: the expert continues
 * into the normal learning form, prefilled, and saves from there.
 *
 * The proposal comes from a model and is labelled as a proposal; the
 * expert's note is the substance.
 */
@Component({
  selector: 'app-learning-draft-dialog',
  templateUrl: './learning-draft-dialog.component.html',
  styleUrl: './learning-draft-dialog.component.scss',
})
export class LearningDraftDialogComponent {
  @Input() eventId = '';
  @Input() question = '';
  @Input() answer = '';
  @Input() rating: 'up' | 'down' | null = null;
  @Input() feedbackText: string | null = null;
  /** Whether the caller may write client-wide learnings (admin/manager). */
  @Input() canWriteClient = false;
  @Input() onProceed: (result: LearningDraftResult) => void = () => {};

  readonly categories: AnalysisCategory[] = [
    'knowledge_gap',
    'wrong_material',
    'delivery',
    'misunderstood',
    'out_of_scope',
    'guardrail_side_effect',
    'not_agent_fault',
  ];

  expertNote = '';
  loading = false;
  failed = false;
  draft: LearningDraft | null = null;
  category: AnalysisCategory | null = null;
  scope: 'bot' | 'client' = 'bot';
  courseOnly = false;

  constructor(
    protected dialogRef: NbDialogRef<LearningDraftDialogComponent>,
    private learningService: LearningService
  ) {}

  get duplicate(): SimilarLearning | null {
    const id = this.draft?.proposal.duplicate_of;
    return id ? this.draft!.similar.find((s) => s.learning_id === id) ?? null : null;
  }

  requestDraft(form: NgForm): void {
    if (this.draft) return; // step 2 has no submit; Enter must not re-draft
    if (form.invalid || this.expertNote.trim().length < 3) {
      form.form.markAllAsTouched();
      return;
    }
    this.loading = true;
    this.failed = false;
    this.learningService.draftFromTurn(this.eventId, this.expertNote.trim()).subscribe({
      next: (draft) => {
        this.draft = draft;
        this.category = draft.proposal.analysis_category;
        this.scope = draft.proposal.scope === 'client' && this.canWriteClient ? 'client' : 'bot';
        this.courseOnly = false;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.failed = true;
      },
    });
  }

  back(): void {
    this.draft = null;
  }

  proceed(): void {
    const d = this.draft;
    if (!d?.turn.bot_id) return;
    const p = d.proposal;
    // The learner's own wording is kept as a 'question' anchor; the model's
    // variants are 'paraphrase'. The backend drops an anchor that equals the
    // learning's own question, so nothing is embedded twice.
    const anchors = p.anchors.map((text) => ({
      text,
      kind: this.same(text, d.turn.question) ? ('question' as const) : ('paraphrase' as const),
    }));
    this.onProceed({
      scope: this.scope,
      botId: d.turn.bot_id,
      prefill: {
        type: p.type,
        topic: p.topic,
        question: p.type === 'qa_example' ? p.question || d.turn.question : null,
        content: p.content,
        tags: [],
        is_active: true,
        anchors,
        // Course-bound only when the expert says so — most corrections hold
        // beyond the course the conversation happened in.
        course_id: this.courseOnly ? d.turn.course_id ?? null : null,
        review_at: d.review_at ?? null,
        valid_until: null,
        applies_to_templates: null,
        source_event_id: d.turn.event_id,
        expert_note: this.expertNote.trim(),
        analysis_category: this.category,
      },
    });
    this.dialogRef.close();
  }

  close(): void {
    this.dialogRef.close();
  }

  private same(a: string, b: string): boolean {
    const n = (s: string) => s.trim().replace(/\s+/g, ' ').toLocaleLowerCase();
    return !!a && !!b && n(a) === n(b);
  }
}
